import type { User } from '@/types'

/** The crew shell's route. */
export const CREW_PATH = '/crew'

/** The one `users.department` value that routes to the crew shell. */
export const FIELD_DEPARTMENT = 'Field'

/**
 * The overseas cohort's `users.department` value — the fifth legal value,
 * minted by migration `d4a7f2c9e6b3` and mirrored in the admin allowlist at
 * `backend/app/routers/users.py:28`. Spelled exactly: the CHECK constraint is
 * case- and whitespace-sensitive, so `'overseas'` and `'Overseas '` are both
 * rejected at the setter.
 */
export const OVERSEAS_DEPARTMENT = 'Overseas'

/**
 * Whether this user's home is the crew shell.
 *
 * `department` is a reporting/UX bucket, NOT a capability — it decides which
 * front door someone lands at and nothing else. Permissions stay on the flags,
 * and a Field user who navigates to a full-app route is never bounced back.
 */
export function isFieldUser(user: User | null | undefined): boolean {
  return user?.department === FIELD_DEPARTMENT
}

/**
 * Whether this user is in the overseas cohort.
 *
 * Same bucket semantics as `isFieldUser`: a reporting/UX value, never a
 * permission. Overseas staff keep every full-app route they had — this only
 * decides which chrome is worth showing them.
 */
export function isOverseasUser(user: User | null | undefined): boolean {
  return user?.department === OVERSEAS_DEPARTMENT
}

/**
 * Whether the NavBar's bare-clock button should be hidden for this user.
 *
 * Two cohorts, one predicate, for two different reasons:
 *
 * - **Field** punch through the crew shell, where a punch also opens/closes a
 *   trip segment. The NavBar button punches the bare clock only, so a crew
 *   member using it starts a day with no segment behind it.
 * - **Overseas** do not punch at all, so a clock affordance is not a wrong
 *   button for them — it is a button with nothing behind it.
 *
 * Deliberately separate from `isFieldUser`: the redirects still route on
 * `Field` alone, and widening this predicate must not widen those.
 */
export function hidesBareClock(user: User | null | undefined): boolean {
  return isFieldUser(user) || isOverseasUser(user)
}

/**
 * Whether this user gets the crew-mode toggle.
 *
 * Keyed on the `is_field_crew` capability flag, NOT on `department` (owner
 * ruling 2026-08-04, D-105): being able to work a crew shift is a capability,
 * and `department` is only the front door you land at. This is what lets the
 * field-capable hybrids who report to no field department — Luis Rojas and
 * Michael Newberg, `department` NULL by the seed's own ruling
 * (`e5b3c8a1d9f2:96-102`) — reach crew mode from the full app.
 *
 * The flag is a superset of the `Field` bucket today: all seven seeded Field
 * users carry it, so the re-key strands nobody.
 */
export function isCrewCapable(user: User | null | undefined): boolean {
  return user?.is_field_crew === true
}

/** Where a just-authenticated user belongs. */
export function landingPathFor(user: User | null | undefined): string {
  return isFieldUser(user) ? CREW_PATH : '/'
}
